import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, AggridSwitchButton, aggridSwitchButtonWrapperStyle, ALLOWED_ITEM_ACCOUNTS, SmallTitleSpan, MaximizeIcon, RestoreIcon } from "src/components/common/common";
import { GridCalendar } from "src/components/common/form/GridCalendar";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { createCalendarCellClickHandler, CreateCellClickHandler, createCellCssClassRules, createGridDropDownCommitAfter, CustomCellClickAction, getSwitchButtonColumnOptions, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags  } from "src/utils/grid/gridStyleUtils";
import { defaultBomData, MST1020_R01_BomDto } from "./Dto_MST1020_R01";
import { useMST1020_R01Store, useMST1020_R01Store_GridCalendar } from "./Store_MST1020_R01";
import { useBomCellChange } from './hooks/useBomCellChange';
import { useBomModal } from './hooks/useBomModal';
import { CommonCodeColumn, getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";
import { useGridExcelUpload } from "src/hooks/useGridExcelUpload";
import { createMST1020ExcelConfig, MST1020_R01_EXCEL_HEADER_NAMES } from "./ExcelConfig_MST1020_R01";

const COLUMN_STATE_KEY = 'MST1020_R01-bom-grid-column-state';


interface Props {
	gridRef: React.RefObject<AgGridReact<MST1020_R01_BomDto> | null>;
	defaultMST1020_R01BomRow: MST1020_R01_BomDto;
	isMaximized?: boolean;
	onToggleMaximize?: () => void;
}

export const DetailTable_MST1020_R01 = (props: Props) => {

	const { gridRef, defaultMST1020_R01BomRow, isMaximized = false, onToggleMaximize } = props;

	const { 
		MST1020_R01BomList, setMST1020_R01BomList,
		selectedMST1020_R01BomDetail, setSelectedMST1020_R01BomDetail, 
		selectedMST1020_R01BomDetailList, setSelectedMST1020_R01BomDetailList, initSelectedMST1020_R01BomDetailList,
		addSelectedMST1020_R01BomDetailList, removeSelectedMST1020_R01BomDetailList,
		deletedMST1020_R01BomDetailList, addDeletedMST1020_R01BomDetailList,
		bomColumnState, setBomColumnState,
		selectedMST1020_R01Item,
		originalMST1020_R01BomList, setOriginalMST1020_R01BomList,
		insertMST1020_R01BomList, removeMST1020_R01Bom, updateMST1020_R01Bom,
		detailScrollTop, setDetailScrollTop,
	} = useMST1020_R01Store();
	
	const { unitList, materialIntypeList, itemCdMap } = useCommonCodeStore();
	const [clickable, setClickable] = useState(true);
// GridCalendar 관련 상태
	const { MST1020_R01GridCalendarState, setMST1020_R01GridCalendarState, initMST1020_R01GridCalendarState } = useMST1020_R01Store_GridCalendar();
	const { pickerVisible, pickerPosition, targetRowIndex, targetField, targetDate } = MST1020_R01GridCalendarState;
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const { 
		getRowId,
		settingRowIndex, 
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		handleSwitchButtonHandler,
	} = useGridSettingContent<MST1020_R01_BomDto>({
		gridRef,
		key: 'valueIndex',
		gridData: MST1020_R01BomList,
		defaultGridData: defaultBomData,
		selectedRow: selectedMST1020_R01BomDetail,
		originalGridData: originalMST1020_R01BomList,
		updateRow: (index: number, data: MST1020_R01_BomDto) => updateMST1020_R01Bom(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['prntItemcd', 'prntItemnm', 'childItemnm', 'itemSpec', 'locationNm', 'applyBeginDt', 'applyEndDt', ],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],
		
		pointerCells: [ 'childItemcd', 'childItemnm', 'itemSpec', 'bomSeq', 'bomUseyn', 'prntItemQty', 'prntItemUnit', 'childItemUsage', 'childItemUnit', 'fixLossqty', 'lossPercent', 'remark'],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		
		alignLeftCells: ['']
	}), []);

	const headerNames = MST1020_R01_EXCEL_HEADER_NAMES;

	// 자재공급방법 공통코드가 조회되면 템플릿 안내표에도 반영되도록 Excel 설정을 다시 생성한다.
	const excelConfig = useMemo(
		() => createMST1020ExcelConfig(
			selectedMST1020_R01Item ?? null,
			defaultMST1020_R01BomRow,
			itemCdMap,
			materialIntypeList
		),
		[selectedMST1020_R01Item, defaultMST1020_R01BomRow, itemCdMap, materialIntypeList]
	);

	const {
		fileInputRef,
		handleDownloadTemplate,
		handleUploadButtonClick,
		handleExcelUpload,
	} = useGridExcelUpload(excelConfig, MST1020_R01BomList, setMST1020_R01BomList);

	
	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<MST1020_R01_BomDto>({
            idKey: 'valueIndex',
            selectedList: selectedMST1020_R01BomDetailList,
            deletedList: deletedMST1020_R01BomDetailList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
        selectedMST1020_R01BomDetailList, deletedMST1020_R01BomDetailList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
    ]);


	// useBomModal 훅 적용
	const { settingCodeSearchModalProps } = useBomModal({
		updateMST1020_R01Bom,
		gridRef,
		MST1020_R01BomList,
	});

	// useBomCellChange 훅 적용
	const { handleCellChange } = useBomCellChange({
		MST1020_R01BomList,
		updateMST1020_R01Bom,
		originalMST1020_R01BomList,
		settingCodeSearchModalProps,
		gridRef,
	});

	const handleRowClick = useRowSelectionHandler({
        dataList: MST1020_R01BomList,
        idKey: 'valueIndex',
        selectedList: selectedMST1020_R01BomDetailList,
        deletedList: deletedMST1020_R01BomDetailList,
        addSelected: addSelectedMST1020_R01BomDetailList,
        removeSelected: removeSelectedMST1020_R01BomDetailList,
        clickable, setClickable
    });

	const dateColumns = ['applyBeginDt', 'applyEndDt'];
	const commonInputColumns = ['prntItemUnit', 'childItemUnit', 'bomSeq', 'prntItemQty', 'childItemUsage', 'fixLossqty', 'lossPercent', 'remark'];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['childItemcd', 'bomSeq', 'prntItemQty',  'childItemUsage', 'fixLossqty', 'materialIntype', 'lossPercent', 'remark'];

	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: MST1020_R01BomList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});

	// 공통 편집 가능 여부 체크 함수
	const isEditableRow = useCallback((params: any): boolean => {

		// 새로 추가된 행(inserted: true)은 편집 가능
		if (params.data?.inserted) return true;

		// 조회 시 원본 데이터의 사용여부가 'N'인 행은 편집 불가
		const originalData = originalMST1020_R01BomList.find(item => item.valueIndex === params.data?.valueIndex);
		const originalBomUseyn = originalData?.bomUseyn || 'N';
		if (originalBomUseyn === 'N') return false;
		
		// 삭제된 행은 편집 불가
		if (params.data?.childItemcd && deletedMST1020_R01BomDetailList.includes(params.data.childItemcd)) return false;
		
		return true;
	}, [originalMST1020_R01BomList, deletedMST1020_R01BomDetailList]);


	// 공통 검증 함수 (날짜 컬럼 클릭 시)
	const validateDateColumns = useCallback((field: string, data: any): boolean => {
		const originalData = originalMST1020_R01BomList.find(item => item.valueIndex === data.valueIndex);
		const originalBomUseyn = originalData?.bomUseyn || 'N';
		return dateColumns.includes(field) && originalBomUseyn !== 'N';
	}, [originalMST1020_R01BomList, dateColumns]);
	
	const cellClickActions: CustomCellClickAction<MST1020_R01_BomDto>[] = useMemo(() => [

		{
            validate: validateDateColumns,
            // 날짜 피커 열기
            action: createCalendarCellClickHandler('applyBeginDt', MST1020_R01GridCalendarState, setMST1020_R01GridCalendarState),
        },
        {
            validate: (field, data) => commonInputColumns.includes(field) && data.bomUseyn !== 'N',
            //aggrid 기본 편집 기능
            action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
        },
	], [
		MST1020_R01GridCalendarState, settingCodeSearchModalProps,
	]);
	const handleCellClick = CreateCellClickHandler(MST1020_R01BomList, 'valueIndex', cellClickActions);

	// 1. 더블클릭 핸들러 추가
	const handleCellDoubleClick = useCallback((e: any) => {
		if ((e.colDef.field === 'childItemcd' || e.colDef.field === 'childItemnm' || e.colDef.field === 'itemSpec') && isEditableRow(e)) {
				settingCodeSearchModalProps(
				'품목',
				e.data.childItemcd || '',
				false,
				'childItemcd',
				e.data,
				e.data.valueIndex!,
				{ itemAccount: ALLOWED_ITEM_ACCOUNTS }
			);
			}
	}, [settingCodeSearchModalProps, isEditableRow]);

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
		InsertRowHandler({
			event: e,
			dataList: MST1020_R01BomList,
			rowData: MST1020_R01BomList[rowIndex],
			insertRow: insertMST1020_R01BomList,
			defaultRowData: defaultMST1020_R01BomRow,
			setClickable
		});
	}, [MST1020_R01BomList, insertMST1020_R01BomList, defaultMST1020_R01BomRow, setClickable]);

	const handleGridDropDownCommitAfter = useMemo(() => 
		createGridDropDownCommitAfter<MST1020_R01_BomDto>(defaultEditableColumns),
	[defaultEditableColumns],);

	const baseColumnDefs: ColDef<MST1020_R01_BomDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: MST1020_R01_BomDto) => renderIndexCell(
				params, 
				(innerParams: MST1020_R01_BomDto) => getRowStyleFlags (
					innerParams,
					{
						deletedList: deletedMST1020_R01BomDetailList,
						selectedList: selectedMST1020_R01BomDetailList,
						dataList: MST1020_R01BomList
					}),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, e); }
					} else { 
						insertRowHandler(rowIndex, e);
					}
				},
				(rowData) => RemoveRowHandler( 
					gridRef,
					MST1020_R01BomList, 
					rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
					setClickable, 
					removeMST1020_R01Bom, 
					(valueIndex) => addDeletedMST1020_R01BomDetailList([valueIndex.toString()])
				),
			)
		),
		{
			headerName: '모품목코드', colId: 'prntItemcd', field: 'prntItemcd', width: 120,
			editable: false
		},
		{
			headerName: '모품목명', colId: 'prntItemnm', field: 'prntItemnm', width: 180,
			editable: false
		},
		{
			headerName: '자품목코드', colId: 'childItemcd', field: 'childItemcd', width: 120,
			editable: isEditableRow,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
		},
		{
			headerName: '자품목명', colId: 'childItemnm', field: 'childItemnm', width: 180,
			editable: false
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150,
			editable: false
		},
		{
			headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
			editable: false,
		} as ColDef<MST1020_R01_BomDto>,
		{
			headerName: '순서', colId: 'bomSeq', field: 'bomSeq', width: 80,
			editable: isEditableRow,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '사용여부', colId: 'bomUseyn', field: 'bomUseyn', width: 80,
			...getSwitchButtonColumnOptions('bomUseyn', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
		},
		{
			headerName: '적용시작일', colId: 'applyBeginDt', field: 'applyBeginDt', width: 120,
			
			editable: false, // GridCalendar로 처리하므로 false
			cellRenderer: (params: any) => {
				if (!params.value) return '';
				const date = new Date(params.value);
				return date.toLocaleDateString('ko-KR', { 
					year: 'numeric', 
					month: '2-digit', 
					day: '2-digit'
				});
			}
		},
		{
			headerName: '적용종료일', colId: 'applyEndDt', field: 'applyEndDt', width: 120,
			
			editable: false, // GridCalendar로 처리하므로 false
			cellRenderer: (params: any) => {
				if (!params.value) return '';
				const date = new Date(params.value);
				return date.toLocaleDateString('ko-KR', { 
					year: 'numeric', 
					month: '2-digit', 
					day: '2-digit'
				});
			}
		},
		{
			headerName: '모품목수량', colId: 'prntItemQty', field: 'prntItemQty', width: 100,
			editable: isEditableRow,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '모품목단위', colId: 'prntItemUnit', field: 'prntItemUnit', width: 140,
			...CommonCodeColumn.options(unitList, isEditableRow, CommonCodeColumn.change(updateMST1020_R01Bom), handleGridDropDownCommitAfter),
		},
		{
			headerName: '소요량', colId: 'childItemUsage', field: 'childItemUsage', width: 100,
			editable: isEditableRow,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '자품목단위', colId: 'childItemUnit', field: 'childItemUnit', width: 140,
			...CommonCodeColumn.options(unitList, isEditableRow, CommonCodeColumn.change(updateMST1020_R01Bom), handleGridDropDownCommitAfter),
		},
		{
			headerName: '자재공급방법', colId: 'materialIntype', field: 'materialIntype', width: 150,
			...CommonCodeColumn.options(materialIntypeList, isEditableRow, CommonCodeColumn.change(updateMST1020_R01Bom), handleGridDropDownCommitAfter),
		},
		{
			headerName: '고정손실량', colId: 'fixLossqty', field: 'fixLossqty', width: 100,
			editable: isEditableRow,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '손실율(%)', colId: 'lossPercent', field: 'lossPercent', width: 100,
			editable: isEditableRow, // 사용자가 직접 입력
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', 
			editable: isEditableRow,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
		},
	], [MST1020_R01BomList, selectedMST1020_R01BomDetailList, unitList, isEditableRow]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef[] = useMemo(() => {
		if (bomColumnState === null) {
			return baseColumnDefs;
		}

		return GridCustomColumnDefs<MST1020_R01_BomDto>({
			baseColumnDefs,
			columnState: bomColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [bomColumnState, settingRowIndex]);

	const saveColumnState = createSaveColumnState(gridRef, COLUMN_STATE_KEY, setBomColumnState);


	const handlePostSort = useCallback((params: PostSortRowsParams<MST1020_R01_BomDto>) => {
		// 드래그 중에는 postSortRows에 의한 강제 정렬을 막아 드래그가 눈에 보이도록 함
		if (isRowDraggingRef.current) return;
		postSortHandler(params, MST1020_R01BomList, originalMST1020_R01BomList, setMST1020_R01BomList);
	}, [MST1020_R01BomList, originalMST1020_R01BomList, ]);

	const pinnedBottomRow = useMemo(() => {
		// 손실율 평균 계산 (입력된 lossPercent 기준)
		const lossPercentList = MST1020_R01BomList
			.map(item => item.lossPercent || 0)
			.filter(loss => loss > 0);
		const averageLossPercent = lossPercentList.length > 0 
			? lossPercentList.reduce((sum, loss) => sum + loss, 0) / lossPercentList.length 
			: 0;

		return [{
			rowIndex: '계',
			childItemcd: MST1020_R01BomList.length.toLocaleString() + "건",
			childItemUsage: MST1020_R01BomList.reduce((sum, item) => sum + (item.childItemUsage || 0), 0),
			prntItemQty: MST1020_R01BomList.reduce((sum, item) => sum + (item.prntItemQty || 0), 0),
			lossPercent: Number(averageLossPercent.toFixed(2)), // 평균 Loss율 (소수점 2자리)
			editable: false,
		}];
	}, [MST1020_R01BomList]);

	const [shouldEditNextClick, setShouldEditNextClick] = useState(false);

	const isRowDraggingRef = useRef(false);

	// 셀 클릭 핸들러 래핑
	const handleCellClicked = useCallback((e: any) => {
		if (shouldEditNextClick) {
			setShouldEditNextClick(false);
			setTimeout(() => {
				e.api.stopEditing(); // 강제로 편집 종료
				e.api.startEditingCell({ 
					rowIndex: e.rowIndex,
					colKey: e.colDef.field
				});
			}, 0);
		}
		handleCellClick?.(e);
		e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! });
	}, [shouldEditNextClick, handleCellClick]);

	return (
		<>
			<div className="flex justify-start gap-2 mb-1">
				<input
					type="file"
					ref={fileInputRef}
					onChange={handleExcelUpload}
					style={{ display: 'none' }}
					accept=".xlsx, .xls"
				/>
				<button 
					className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-md text-sm" 
					onClick={handleDownloadTemplate}
					title="엑셀 업로드 양식 다운로드"
				>
					템플릿 다운로드
				</button>
				<button 
					className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-sm" 
					onClick={handleUploadButtonClick}
				>
					Excel 업로드
				</button>
				<button
					className={`relative bg-blue-dark text-white py-[1px] rounded-[4px] px-[8px]`}
					onClick={onToggleMaximize}
					title={isMaximized ? '최소화' : '최대화'}
				>
					{isMaximized ? <RestoreIcon /> : <MaximizeIcon />}
				</button>
			</div>
			<GridContextWrapper<MST1020_R01_BomDto>
				gridWrapperRef={gridWrapperRef}
				gridRef={gridRef}
				paddedGridData={paddedGridData}
				enableCellCalculation
				setPaddedGridData={setMST1020_R01BomList}
				initSelected={initSelectedMST1020_R01BomDetailList}
				defaultRowData={defaultMST1020_R01BomRow}
				baseColumnDefs={bomColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
				setColumnState={setBomColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
			<Grid_Excel<MST1020_R01_BomDto>
					ref={gridRef}
					rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
					columnDefs={columnDefs}		
					defaultColDef={cellCssDefaultColDef}

					rowClassRules={createRowClassRules<MST1020_R01_BomDto>({
						idKey: 'valueIndex',
						selectedList: selectedMST1020_R01BomDetailList,
						deletedList: deletedMST1020_R01BomDetailList,
					})}
					onRowClicked={handleRowClick}
					onCellClicked={handleCellClicked}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellValueChanged={handleCellChange}
					pinnedBottomRowData={pinnedBottomRow}
                    rowDragManaged={true}
                    rowDragEntireRow={true}
					onRowDragEnter={() => { isRowDraggingRef.current = true; }}
                    onRowDragEnd={(e: any) => {

						
						const api = e.api;
						// 정렬 해제하여 사용자가 정한 순서를 유지
						if (api.applyColumnState) {
							api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
						}
						
						const reordered: MST1020_R01_BomDto[] = [];
						api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
							if (!node.data) return;
							const updatedData = {
								...node.data,
								orderIndex: idx + 1,
								orderIndexSeq: 0,
							};

							reordered.push(updatedData);
						});

						setMST1020_R01BomList(reordered);
						isRowDraggingRef.current = false;
					}}

					onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
					// Grid_Select에 공통 로직 처리를 위한 props 전달
					columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
					setColumnState={saveColumnState}				//컬럼 상태 저장
					getDefaultColumnState={() => defaultColumnState}
					scrollTop={detailScrollTop}
					setScrollTop={setDetailScrollTop}
					
				/>
			</GridContextWrapper>
			
			<GridCalendar
				rowIndex={targetRowIndex!}
				targetField={targetField!}
				visible={pickerVisible}
				setVisible={(visible) => setMST1020_R01GridCalendarState({ pickerVisible: visible })}
				value={targetDate}
				pickerPosition={pickerPosition}
				setPickerPosition={(position) => setMST1020_R01GridCalendarState({ pickerPosition: position })}
				onClear={() => {
					setMST1020_R01GridCalendarState({ targetDate: undefined });
				}}
				onChange={(date, rowIndex, targetField) => {
					if (rowIndex !== null && targetField) {
						const foundData = MST1020_R01BomList[rowIndex];
						if (foundData) {
							updateMST1020_R01Bom(foundData.valueIndex!, {
								...foundData,
								[targetField]: date ?? new Date(),
								isModified: true
							});
						}
					}
				}}
				onClickLoad={(rowIndex, targetField) => {
					// 필요시 추가 로직
				}}
			/>
		</>
	);
};
